import { createLink } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { cx } from './cx'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'light' | 'glass'
export type ButtonSize = 'sm' | 'md' | 'lg'

type StyleProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Stretch to the container width (mobile forms, sheets). */
  block?: boolean
}

const base =
  'relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium ' +
  'transition-[background-color,border-color,color,opacity] duration-150 ease-out ' +
  'disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45'

const variants: Record<ButtonVariant, string> = {
  // Primary is ink, not accent: the accent is reserved for "active/selected" and focus.
  primary: 'bg-ink text-ink-inverse hover:bg-ink/85 active:bg-ink/75',
  secondary: 'border border-line-strong bg-surface text-ink hover:bg-bg-hover active:bg-bg-hover',
  ghost: 'text-ink-2 hover:bg-bg-hover hover:text-ink active:bg-bg-hover',
  danger: 'bg-negative text-ink-inverse hover:bg-negative/90 active:bg-negative/80',
  // On a Stage (a dark picture) in either theme: light is the primary there, glass the secondary.
  light: 'bg-white text-zinc-950 hover:bg-white/85 active:bg-white/75',
  glass: 'bg-white/10 text-white ring-1 ring-white/25 ring-inset backdrop-blur-md hover:bg-white/20 active:bg-white/25',
}

// md/lg grow on touch screens so every target is ≥ 44 px; sm stays compact for dense table rows.
const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-small [&_svg]:size-4',
  md: 'h-11 px-4 text-body pointer-fine:h-9 pointer-fine:px-3.5 pointer-fine:text-small [&_svg]:size-4',
  lg: 'h-12 px-5 text-body [&_svg]:size-5',
}

export function buttonClass({ variant = 'secondary', size = 'md', block }: StyleProps = {}) {
  return cx(base, variants[variant], sizes[size], block && 'w-full')
}

type ContentProps = {
  icon?: LucideIcon | undefined
  iconEnd?: LucideIcon | undefined
  loading?: boolean | undefined
  children?: ReactNode
}

function Content({ icon: Icon, iconEnd: IconEnd, loading, children }: ContentProps) {
  return (
    <>
      {/* Keep the label in the layout (and the accessible name: opacity, not visibility) while loading. */}
      <span className={cx('inline-flex items-center gap-2', loading && 'opacity-0')}>
        {Icon && <Icon aria-hidden="true" strokeWidth={2} />}
        {children}
        {IconEnd && <IconEnd aria-hidden="true" strokeWidth={2} />}
      </span>
      {loading && (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner size="sm" />
        </span>
      )}
    </>
  )
}

export type ButtonProps = Omit<ComponentProps<'button'>, 'className'> &
  StyleProps &
  ContentProps & { className?: string }

export function Button({
  variant,
  size,
  block,
  icon,
  iconEnd,
  loading,
  children,
  className,
  type = 'button',
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      aria-busy={loading || undefined}
      // Loading is not disabled: the button keeps its colour and focus, it just ignores presses
      // (preventDefault also stops a submit button from re-submitting its form).
      onClick={(e) => (loading ? e.preventDefault() : onClick?.(e))}
      className={cx(buttonClass({ variant, size, block }), loading && 'cursor-progress', className)}
      {...rest}
    >
      <Content icon={icon} iconEnd={iconEnd} loading={loading}>
        {children}
      </Content>
    </button>
  )
}

type AnchorProps = Omit<ComponentProps<'a'>, 'className'> & StyleProps & Omit<ContentProps, 'loading'>

function ButtonAnchor({ variant, size, block, icon, iconEnd, children, ...rest }: AnchorProps) {
  return (
    <a className={buttonClass({ variant, size, block })} {...rest}>
      <Content icon={icon} iconEnd={iconEnd}>
        {children}
      </Content>
    </a>
  )
}

/** Router link that looks like a button: `<ButtonLink to="/training/$id" params={{ id }} variant="primary">`. */
export const ButtonLink = createLink(ButtonAnchor)

const iconSizes: Record<ButtonSize, string> = {
  sm: 'size-8 [&_svg]:size-4',
  md: 'size-11 pointer-fine:size-9 [&_svg]:size-[18px]',
  lg: 'size-12 [&_svg]:size-5',
}

export function iconButtonClass({ variant = 'ghost', size = 'md' }: { variant?: ButtonVariant; size?: ButtonSize } = {}) {
  return cx(
    base,
    variants[variant],
    iconSizes[size],
    'group/icon',
    // A small sm button still gets a 44 px hit area via an invisible inset on touch screens.
    size === 'sm' && 'pointer-coarse:after:absolute pointer-coarse:after:-inset-1.5',
  )
}

export type IconButtonProps = Omit<ComponentProps<'button'>, 'className' | 'children' | 'aria-label'> & {
  /** Required accessible name; also shown as a tooltip on hover and keyboard focus. */
  label: string
  icon: LucideIcon
  variant?: Exclude<ButtonVariant, 'primary' | 'danger'>
  size?: ButtonSize
  /** Show the label tooltip (default true). Turn off where the name is visible next to the button. */
  tooltip?: boolean
  className?: string
}

export function IconButton({
  label,
  icon: Icon,
  variant = 'ghost',
  size = 'md',
  tooltip = true,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      className={cx(iconButtonClass({ variant, size }), className)}
      {...rest}
    >
      <Icon aria-hidden="true" strokeWidth={2} />
      {tooltip && <TipBubble>{label}</TipBubble>}
    </button>
  )
}

/** Hover/focus label bubble. aria-hidden: the owner carries the accessible name. */
export function TipBubble({ children, side = 'top' }: { children: ReactNode; side?: 'top' | 'bottom' }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        // display:none while hidden: an invisible bubble at the screen edge would still widen the page.
        'pointer-events-none absolute left-1/2 z-30 hidden -translate-x-1/2 animate-fade whitespace-nowrap rounded-[6px] bg-ink px-2 py-1 text-label font-medium tracking-normal text-ink-inverse',
        side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
        'group-hover/icon:block group-focus-visible/icon:block group-aria-expanded/icon:hidden!',
      )}
    >
      {children}
    </span>
  )
}
